import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useAppointment,
  useAvailability,
  useBarbers,
  useBenefitPreview,
  useCreateAppointment,
  useCreateRecurrence,
  useRescheduleAppointment,
  useServices,
  useProfile,
  useUpdateProfile,
} from '@/hooks/use-catalog';
import { PageHeader } from '@/components/layout/app-shell';
import {
  ApiError,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Feedback,
  Field,
  LoadingState,
  cx,
  formatDate,
  formatDuration,
  formatMoney,
  toISODate,
  useNow,
  useTenant,
} from '@barber/ui';
import type { AvailabilitySlot, SlotTakenDetails } from '@barber/ui';
import { isValidCpf, normalizeCpf } from '@barber/shared';
import { CalendarDays, Repeat, Scissors } from 'lucide-react';

/**
 * Agendar.
 *
 * ---
 *
 * UM PASSO POR TELA, E NÃO UM FORMULÁRIO GIGANTE
 *
 * Serviço → profissional → dia → horário → confirmar. Cada passo depende do
 * anterior de verdade: os profissionais mudam conforme o serviço, os horários
 * mudam conforme o profissional e o dia. Um formulário único obrigaria a
 * recarregar tudo a cada mudança e mostraria opções que não existem mais.
 *
 * ---
 *
 * O 409 NÃO É UM BECO
 *
 * Entre carregar os horários e confirmar, outra pessoa pode ter agendado. O
 * servidor devolve 409 com ALTERNATIVAS — a tela as oferece em vez de mostrar
 * um erro e mandar o cliente começar de novo. É a diferença entre "deu erro" e
 * "esse foi tomado, que tal 15:30?".
 */

type Passo = 'servico' | 'profissional' | 'horario' | 'confirmar';

/** Quantos dias a grade de datas oferece de uma vez. */
const DIAS_VISIVEIS = 14;

export function BookingPage() {
  const navigate = useNavigate();
  const { tenant, locale } = useTenant();
  const [params] = useSearchParams();

  /** Quando presente, este agendamento está sendo REMARCADO, e não criado. */
  const remarcandoId = params.get('remarcar');

  const [passo, setPasso] = useState<Passo>('servico');
  const [serviceId, setServiceId] = useState<string | null>(params.get('servico'));
  /** Os serviços seguintes da sequência, na ordem em que foram tocados. */
  const [extras, setExtras] = useState<string[]>([]);
  const serviceIds = serviceId ? [serviceId, ...extras] : [];
  const emSequencia = serviceIds.length > 1;
  const [barberId, setBarberId] = useState<string | null>(null);
  const [data, setData] = useState<string>(() => toISODate(new Date(), tenant.timezone));
  const [slot, setSlot] = useState<AvailabilitySlot | null>(null);
  const [repetir, setRepetir] = useState(false);
  const [encaixes, setEncaixes] = useState<string[]>([]);
  /** Pagar adiantado por PIX, com o desconto da barbearia. */
  const [pagarPix, setPagarPix] = useState(false);
  const [cpf, setCpf] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [alternativas, setAlternativas] = useState<readonly { startsAt: string; label: string }[]>(
    [],
  );

  const services = useServices();
  const barbers = useBarbers(serviceId ?? undefined);
  const availability = useAvailability({ serviceIds, date: data, barberId });
  const preview = useBenefitPreview(serviceId, passo === 'confirmar');
  // Todos os profissionais, com o que cada um faz: os encaixes oferecidos (e,
  // na remarcação, os que ficariam de fora) dependem do profissional do slot.
  const todosProfissionais = useBarbers();
  const original = useAppointment(remarcandoId ?? undefined);

  const criar = useCreateAppointment();
  const criarFixo = useCreateRecurrence();
  const perfil = useProfile();
  const salvarPerfil = useUpdateProfile();

  /** O PIX com desconto é só na hora de marcar — remarcar não oferece. */
  const pix = tenant.booking.pix;
  const oferecePix = Boolean(pix?.available) && !remarcandoId;
  const precisaCpf = pagarPix && !perfil.data?.cpfHint;

  /** Horário fixo só para agendamento novo, e só se o dono liberou. */
  const podeRepetir = tenant.booking.allowCustomerRecurrence && !remarcandoId;
  const remarcar = useRescheduleAppointment();

  // A grade de dias começa em HOJE, e "hoje" muda à meia-noite — inclusive com
  // o app aberto na mesa de alguém.
  const agora = useNow(60_000);

  const servico = services.data?.find((s) => s.id === serviceId) ?? null;
  const escolhidos = serviceIds
    .map((id) => services.data?.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  // Sequência: só quem faz TODOS os serviços escolhidos.
  const profissionaisAptos = emSequencia
    ? (todosProfissionais.data ?? []).filter((b) =>
        serviceIds.every((id) => b.serviceIds?.includes(id)),
      )
    : (barbers.data ?? []);
  const profissional = profissionaisAptos.find((b) => b.id === barberId) ?? null;

  /** Os próximos dias, já no fuso da barbearia. */
  const dias = useMemo(() => {
    const limite = Math.min(DIAS_VISIVEIS, tenant.booking.maxAdvanceDays);

    return Array.from({ length: limite }, (_, i) => {
      const date = new Date(agora + i * 86_400_000);
      return { iso: toISODate(date, tenant.timezone), date };
    });
  }, [agora, tenant.booking.maxAdvanceDays, tenant.timezone]);

  /**
   * Trocar de serviço limpa quem foi escolhido antes.
   *
   * A limpeza acontece no HANDLER, e não num efeito que observa `serviceId`.
   * Com o efeito, havia um render intermediário exibindo o profissional antigo
   * junto do serviço novo — e um toque rápido nesse instante mandaria para a
   * API uma combinação que não existe.
   */
  function escolherServico(id: string): void {
    setBarberId(null);
    setSlot(null);

    // Remarcar é sempre de um serviço só: o toque escolhe e avança.
    if (remarcandoId) {
      setServiceId(id);
      setExtras([]);
      setPasso('profissional');
      return;
    }

    // Agendar: o toque marca e desmarca, para dar para levar corte E barba.
    if (id === serviceId) {
      setServiceId(extras[0] ?? null);
      setExtras(extras.slice(1));
    } else if (extras.includes(id)) {
      setExtras(extras.filter((x) => x !== id));
    } else if (!serviceId) {
      setServiceId(id);
    } else if (serviceIds.length < 4) {
      setExtras([...extras, id]);
    }
  }

  function escolherDia(iso: string): void {
    setData(iso);
    setSlot(null);
  }

  function escolherProfissional(id: string | null): void {
    setBarberId(id);
    setSlot(null);
    setPasso('horario');
  }

  async function confirmar(): Promise<void> {
    if (!serviceId || !slot) return;

    setErro(null);
    setAlternativas([]);

    // Sem profissional escolhido ("tanto faz"), fica o primeiro livre NAQUELE
    // horário — a informação vem do próprio slot.
    const escolhido = barberId ?? slot.barberIds[0];

    if (!escolhido) {
      setErro('Este horário acabou de ficar sem profissional. Escolha outro.');
      return;
    }

    // A Redfox exige o CPF de quem paga. Conferido aqui, antes de marcar: um
    // CPF errado descoberto depois deixaria o horário marcado sem o PIX.
    if (precisaCpf && !isValidCpf(cpf)) {
      setErro('CPF inválido. Confira os números.');
      return;
    }

    /** Depois de marcar, o detalhe do horário gera o PIX e mostra o QR. */
    const sufixoPix = pagarPix ? '&pix=1' : '';

    try {
      if (precisaCpf) {
        await salvarPerfil.mutateAsync({ cpf: normalizeCpf(cpf) });
      }

      if (podeRepetir && repetir) {
        // Os encaixes vão no mesmo pedido e entram só na primeira semana — o
        // encaixe não se repete. Mandá-los depois, um a um, deixaria o horário
        // fixo criado pela metade quando a 1ª semana já estivesse no prazo.
        const fixo = await criarFixo.mutateAsync({
          ...(emSequencia ? { serviceIds } : { serviceId }),
          barberId: escolhido,
          startsAt: slot.startsAt,
          ...(encaixesMarcados.length > 0
            ? { addOnServiceIds: encaixesMarcados.map((s) => s.id) }
            : {}),
        });

        // Abre a primeira semana, como num agendamento comum — é o horário
        // que a pessoa acabou de escolher.
        const primeira = fixo.nextOccurrences[0]?.id;
        navigate(primeira ? `/horarios/${primeira}?novo=1${sufixoPix}` : '/horarios', {
          replace: true,
        });
        return;
      }

      const resultado = remarcandoId
        ? await remarcar.mutateAsync({
            id: remarcandoId,
            startsAt: slot.startsAt,
            barberId: escolhido,
          })
        : await criar.mutateAsync({
            ...(emSequencia ? { serviceIds } : { serviceId }),
            barberId: escolhido,
            startsAt: slot.startsAt,
            ...(encaixesMarcados.length > 0
              ? { addOnServiceIds: encaixesMarcados.map((s) => s.id) }
              : {}),
          });

      // Sequência: abre o primeiro serviço marcado.
      const sequencia = (resultado as { appointments?: readonly { id: string }[] }).appointments;
      const aberto = sequencia?.[0]?.id ?? resultado.id;
      navigate(`/horarios/${aberto}?novo=1${sufixoPix}`, { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const detalhes = (error.payload as { details?: SlotTakenDetails })?.details;
        setAlternativas(detalhes?.alternativas ?? []);
        setErro('Alguém pegou este horário primeiro. Veja as opções mais próximas:');
        void availability.refetch();
        return;
      }

      setErro(
        error instanceof ApiError ? error.message : 'Não conseguimos confirmar. Tente novamente.',
      );
    }
  }

  const enviando = criar.isPending || remarcar.isPending || criarFixo.isPending;

  /** Quem vai atender: o escolhido, ou o primeiro livre naquele horário. */
  const atendente = barberId ?? slot?.barberIds[0] ?? null;
  const fazPeloAtendente = new Set(
    todosProfissionais.data?.find((b) => b.id === atendente)?.serviceIds ?? [],
  );

  const encaixesOferecidos = remarcandoId
    ? []
    : (services.data ?? []).filter((s) => s.isAddOn && fazPeloAtendente.has(s.id));
  const encaixesMarcados = encaixesOferecidos.filter((s) => encaixes.includes(s.id));
  const somaEncaixes = encaixesMarcados.reduce((soma, s) => soma + s.priceCents, 0);

  /** O total que a tela mostra — a base do desconto do PIX. */
  const totalEstimado = emSequencia
    ? escolhidos.reduce((t, s) => t + s.priceCents, 0) + somaEncaixes
    : preview.isSuccess && preview.data.coveredByPlan
      ? somaEncaixes
      : (preview.data?.finalPriceCents ?? servico?.priceCents ?? 0) + somaEncaixes;
  const totalNoPix =
    totalEstimado - Math.floor((totalEstimado * (pix?.discountPercent ?? 0)) / 100);

  // Remarcar leva os encaixes que o novo profissional faz; os outros saem.
  // A tela avisa ANTES de confirmar, e não depois.
  const encaixesQueSaem = remarcandoId
    ? (original.data?.addOns ?? []).filter((a) => !fazPeloAtendente.has(a.serviceId))
    : [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={remarcandoId ? 'Remarcar horário' : 'Agendar'}
        description={
          remarcandoId
            ? 'Escolha o novo horário. O anterior é liberado automaticamente.'
            : 'Quatro toques e está feito.'
        }
      />

      <Stepper passo={passo} />

      {/* ------------------------------------------------------- serviço -- */}
      {passo === 'servico' && (
        <section aria-label="Escolha o serviço" className="flex flex-col gap-3">
          {services.isPending && <LoadingState label="Carregando serviços…" />}

          {services.isError && (
            <ErrorState
              description="Não conseguimos carregar os serviços."
              onRetry={() => void services.refetch()}
            />
          )}

          {services.isSuccess && services.data.length === 0 && (
            <EmptyState
              icon={Scissors}
              title="Nenhum serviço disponível"
              description="A barbearia ainda não publicou os serviços dela."
            />
          )}

          {/* Encaixe não é escolhido aqui: ele é oferecido na confirmação. */}
          {services.data
            ?.filter((item) => !item.isAddOn)
            .map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => escolherServico(item.id)}
                aria-pressed={serviceIds.includes(item.id)}
                className={cx(
                  'rounded-card border bg-surface p-4 text-left transition',
                  serviceIds.includes(item.id) ? 'border-brand ring-1 ring-brand' : 'border-border',
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-content">{item.name}</p>
                    {item.description && (
                      <p className="mt-1 text-sm text-content-muted">{item.description}</p>
                    )}
                    <p className="mt-2 text-xs text-content-muted">
                      {formatDuration(item.durationMinutes)}
                    </p>
                  </div>

                  <p className="whitespace-nowrap font-semibold text-content">
                    {formatMoney(item.priceCents, locale)}
                  </p>
                </div>
              </button>
            ))}
          {!remarcandoId && serviceId && (
            <div className="flex flex-col gap-2">
              {emSequencia && (
                <p className="text-sm text-content-muted">
                  Em sequência, com o mesmo profissional:{' '}
                  {escolhidos.map((s) => s.name).join(' + ')} ·{' '}
                  {formatDuration(escolhidos.reduce((t, s) => t + s.durationMinutes, 0))} ·{' '}
                  {formatMoney(
                    escolhidos.reduce((t, s) => t + s.priceCents, 0),
                    locale,
                  )}
                </p>
              )}
              <Button fullWidth onClick={() => setPasso('profissional')}>
                Continuar
              </Button>
            </div>
          )}
        </section>
      )}

      {/* -------------------------------------------------- profissional -- */}
      {passo === 'profissional' && (
        <section aria-label="Escolha o profissional" className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => escolherProfissional(null)}
            className="rounded-card border border-border bg-surface p-4 text-left"
          >
            <p className="font-semibold text-content">Tanto faz</p>
            <p className="mt-1 text-sm text-content-muted">
              Mostra todos os horários livres da barbearia — costuma abrir mais opções.
            </p>
          </button>

          {barbers.isPending && <LoadingState label="Carregando profissionais…" />}

          {profissionaisAptos.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => escolherProfissional(item.id)}
              className="flex items-center gap-3 rounded-card border border-border bg-surface p-4 text-left"
            >
              {item.photoUrl ? (
                <img src={item.photoUrl} alt="" className="h-12 w-12 rounded-full object-cover" />
              ) : (
                <span
                  aria-hidden="true"
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-muted text-sm font-semibold text-content-muted"
                >
                  {item.name.slice(0, 2).toUpperCase()}
                </span>
              )}

              <div className="min-w-0">
                <p className="font-semibold text-content">{item.name}</p>
                {item.specialties.length > 0 && (
                  <p className="truncate text-sm text-content-muted">
                    {item.specialties.join(' · ')}
                  </p>
                )}
              </div>
            </button>
          ))}

          <Button variant="ghost" onClick={() => setPasso('servico')}>
            Voltar
          </Button>
        </section>
      )}

      {/* --------------------------------------------------- dia e hora -- */}
      {passo === 'horario' && (
        <section aria-label="Escolha o horário" className="flex flex-col gap-4">
          <div>
            <p className="mb-2 text-sm font-medium text-content">Dia</p>

            {/*
              Rolagem horizontal com os próximos dias. Um seletor de calendário
              nativo esconderia a informação que importa — quais dias têm vaga —
              atrás de mais um toque.
            */}
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
              {dias.map(({ iso, date }) => (
                <button
                  key={iso}
                  type="button"
                  onClick={() => escolherDia(iso)}
                  aria-pressed={data === iso}
                  className={cx(
                    'tap-target flex min-w-[4.5rem] flex-col items-center rounded-xl border px-3 py-2 text-xs',
                    data === iso
                      ? 'border-brand bg-brand text-brand-fg'
                      : 'border-border bg-surface text-content',
                  )}
                >
                  <span className="uppercase">
                    {new Intl.DateTimeFormat(locale.locale, {
                      weekday: 'short',
                      timeZone: locale.timeZone,
                    }).format(date)}
                  </span>
                  <span className="text-lg font-bold">
                    {new Intl.DateTimeFormat(locale.locale, {
                      day: '2-digit',
                      timeZone: locale.timeZone,
                    }).format(date)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-content">
              Horários de {formatDate(`${data}T12:00:00`, locale)}
            </p>

            {availability.isPending && <LoadingState label="Buscando horários…" />}

            {availability.isError && (
              <ErrorState
                description="Não conseguimos buscar os horários deste dia."
                onRetry={() => void availability.refetch()}
              />
            )}

            {availability.isSuccess && availability.data.slots.length === 0 && (
              <EmptyState
                icon={CalendarDays}
                title="Sem horários neste dia"
                description="Experimente outro dia — ou volte e escolha 'tanto faz' para ver a agenda toda."
              />
            )}

            {availability.data && availability.data.slots.length > 0 && (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {availability.data.slots.map((item) => (
                  <button
                    key={item.startsAt}
                    type="button"
                    onClick={() => {
                      setSlot(item);
                      setPasso('confirmar');
                    }}
                    className={cx(
                      'tap-target rounded-xl border py-3 text-sm font-semibold transition',
                      slot?.startsAt === item.startsAt
                        ? 'border-brand bg-brand text-brand-fg'
                        : 'border-border bg-surface text-content',
                    )}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Button variant="ghost" onClick={() => setPasso('profissional')}>
            Voltar
          </Button>
        </section>
      )}

      {/* -------------------------------------------------------- resumo -- */}
      {passo === 'confirmar' && servico && slot && (
        <section aria-label="Confirmar agendamento" className="flex flex-col gap-4">
          <Card className="flex flex-col gap-3">
            <Linha
              rotulo={emSequencia ? 'Serviços' : 'Serviço'}
              valor={escolhidos.map((s) => s.name).join(' + ')}
            />
            <Linha rotulo="Profissional" valor={profissional?.name ?? 'Primeiro disponível'} />
            <Linha rotulo="Dia" valor={formatDate(slot.startsAt, locale)} />
            <Linha rotulo="Horário" valor={slot.label} />
            <Linha
              rotulo="Duração"
              valor={formatDuration(escolhidos.reduce((t, s) => t + s.durationMinutes, 0))}
            />

            <div className="border-t border-border pt-3">
              {emSequencia ? (
                // Sequência: o benefício do plano é aplicado serviço a serviço
                // na hora de marcar; aqui vai o preço de tabela somado.
                <div className="flex items-center justify-between">
                  <span className="text-sm text-content">Total</span>
                  <p className="text-lg font-bold text-content">
                    {formatMoney(
                      escolhidos.reduce((t, s) => t + s.priceCents, 0) + somaEncaixes,
                      locale,
                    )}
                  </p>
                </div>
              ) : preview.isSuccess && preview.data.coveredByPlan ? (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-content">Total</span>
                  <div className="text-right">
                    <Badge tone="success">Incluso no plano</Badge>
                    {somaEncaixes > 0 && (
                      <p className="mt-1 text-sm font-semibold text-content">
                        + {formatMoney(somaEncaixes, locale)} de encaixe
                      </p>
                    )}
                    {preview.data.remaining !== undefined && (
                      <p className="mt-1 text-xs text-content-muted">
                        Restam {preview.data.remaining} de {preview.data.of} neste ciclo
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-content">Total</span>
                  <div className="text-right">
                    <p className="text-lg font-bold text-content">
                      {formatMoney(
                        (preview.data?.finalPriceCents ?? servico.priceCents) + somaEncaixes,
                        locale,
                      )}
                    </p>
                    {preview.data &&
                      preview.data.finalPriceCents < preview.data.originalPriceCents && (
                        <p className="text-xs text-content-muted">
                          <span className="line-through">
                            {formatMoney(preview.data.originalPriceCents, locale)}
                          </span>{' '}
                          {preview.data.discountLabel}
                        </p>
                      )}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {encaixesOferecidos.length > 0 && (
            <Card className="flex flex-col gap-3">
              <p className="text-sm font-semibold text-content">Aproveitar e fazer também</p>
              {encaixesOferecidos.map((s) => (
                <label key={s.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={encaixes.includes(s.id)}
                      onChange={(event) =>
                        setEncaixes((atual) =>
                          event.target.checked
                            ? [...atual, s.id]
                            : atual.filter((id) => id !== s.id),
                        )
                      }
                      className="h-5 w-5 rounded border-border text-brand"
                    />
                    {s.name}
                  </span>
                  <span className="text-content-muted">+ {formatMoney(s.priceCents, locale)}</span>
                </label>
              ))}
              <p className="text-xs text-content-muted">
                Feito entre um atendimento e outro: não aumenta o tempo do seu horário.
              </p>
            </Card>
          )}

          {encaixesQueSaem.length > 0 && (
            <p
              role="status"
              className="rounded-xl border border-sx-warn/40 bg-sx-warn/10 px-4 py-3 text-sm text-content"
            >
              Com este profissional, estes encaixes ficam de fora:{' '}
              {encaixesQueSaem.map((a) => a.service.name).join(', ')}.
            </p>
          )}

          {podeRepetir && (
            <label className="flex items-start gap-3 rounded-card border border-border bg-surface p-4">
              <input
                type="checkbox"
                checked={repetir}
                onChange={(event) => setRepetir(event.target.checked)}
                className="mt-1 h-5 w-5 rounded border-border text-brand"
              />
              <span className="text-sm text-content">
                <span className="flex items-center gap-1 font-semibold">
                  <Repeat className="h-4 w-4" aria-hidden="true" />
                  Repetir toda semana neste horário
                </span>
                <span className="mt-1 block text-content-muted">
                  A cadeira fica reservada pra você toda semana, no mesmo dia e às {slot.label}. Se
                  não puder vir num dia, cancele só aquela semana.
                </span>
              </span>
            </label>
          )}

          {oferecePix && totalEstimado > 0 && (
            <div className="flex flex-col gap-3 rounded-card border border-border bg-surface p-4">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={pagarPix}
                  onChange={(event) => setPagarPix(event.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-border text-brand"
                />
                <span className="text-sm text-content">
                  <span className="font-semibold">
                    Pagar agora por PIX com {pix?.discountPercent}% de desconto
                  </span>
                  <span className="mt-1 block text-content-muted">
                    {formatMoney(totalNoPix, locale)} no PIX. Se não pagar agora, o horário continua
                    marcado e você paga no dia, sem o desconto.
                  </span>
                </span>
              </label>

              {precisaCpf && (
                <Field
                  label="CPF de quem paga"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="000.000.000-00"
                  hint="O banco exige o CPF no PIX. Fica salvo no seu perfil."
                  value={cpf}
                  onChange={(event) => setCpf(event.target.value)}
                />
              )}
            </div>
          )}

          {erro && <Feedback tone="error">{erro}</Feedback>}

          {alternativas.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {alternativas.map((alternativa) => (
                <button
                  key={alternativa.startsAt}
                  type="button"
                  onClick={() => {
                    setSlot({
                      startsAt: alternativa.startsAt,
                      endsAt: alternativa.startsAt,
                      label: alternativa.label,
                      barberIds: barberId ? [barberId] : [],
                    });
                    setErro(null);
                    setAlternativas([]);
                  }}
                  className="tap-target rounded-xl border border-brand bg-surface py-3 text-sm font-semibold text-brand"
                >
                  {alternativa.label}
                </button>
              ))}
            </div>
          )}

          <Button fullWidth loading={enviando} onClick={() => void confirmar()}>
            {remarcandoId ? 'Confirmar remarcação' : 'Confirmar agendamento'}
          </Button>

          <Button variant="ghost" onClick={() => setPasso('horario')} disabled={enviando}>
            Escolher outro horário
          </Button>
        </section>
      )}
    </div>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-content-muted">{rotulo}</span>
      <span className="font-medium text-content">{valor}</span>
    </div>
  );
}

const PASSOS: readonly { id: Passo; label: string }[] = [
  { id: 'servico', label: 'Serviço' },
  { id: 'profissional', label: 'Profissional' },
  { id: 'horario', label: 'Horário' },
  { id: 'confirmar', label: 'Confirmar' },
];

function Stepper({ passo }: { passo: Passo }) {
  const atual = PASSOS.findIndex((p) => p.id === passo);

  return (
    <ol className="flex items-center gap-2" aria-label={`Passo ${atual + 1} de ${PASSOS.length}`}>
      {PASSOS.map((item, indice) => (
        <li key={item.id} className="flex flex-1 flex-col gap-1">
          <div className={cx('h-1 rounded-full', indice <= atual ? 'bg-brand' : 'bg-border')} />
          <span
            className={cx(
              'text-[11px]',
              indice === atual ? 'font-semibold text-content' : 'text-content-muted',
            )}
            aria-current={indice === atual ? 'step' : undefined}
          >
            {item.label}
          </span>
        </li>
      ))}
    </ol>
  );
}
